import * as Sentry from "@sentry/browser";
import type { ValidationErrors } from "final-form";
import { type JSX, useCallback, useState } from "react";
import { Form } from "react-final-form";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { useMutation } from "urql";
import RenderContent from "../../shared/components/RenderContent";
import t from "../../shared/translations";
import type { ContentFormat } from "../../shared/types";
import Input from "../components/FinalInput";
import Textarea from "../components/FinalTextarea";
import { graphql } from "../gql";
import { closeModal, type Dispatch } from "../state";

interface IFormValues {
    subject: string;
    content: string;
}

const defaultValues: IFormValues = {
    content: "",
    subject: "",
};

const sendMutation = graphql(`
    mutation AppMessageSendMutation($subject: String!, $content: String!, $replyToId: ID) {
        messageSend(subject: $subject, content: $content, replyToId: $replyToId) {
            error
            learningCoachMessage { id }
        }
    }
`);

function validate(values: IFormValues): ValidationErrors {
    const errors: ValidationErrors = {};
    if (!values.subject) {
        errors.subject = t("player.messages.form-message-error-subject");
    }
    if (!values.content) {
        errors.content = t("player.messages.form-message-error-content");
    }
    return errors;
}

interface IProps {
    message?: {
        id: string;
        datetime: string;
        senderName: string;
        subject: string;
        content: string;
        contentFormat: ContentFormat;
    };
}

const H3 = styled.h3`
    font-size: 1.2rem;
`;

function Send({ message }: IProps): JSX.Element {
    const [_, executeMutation] = useMutation(sendMutation);
    const dispatch = useDispatch<Dispatch>();
    const [showError, setShowError] = useState(false);

    const onSubmit = useCallback(
        async (values: IFormValues) => {
            const result = await executeMutation({
                replyToId: message?.id || null,
                ...values,
            });
            if (result.error) {
                console.error(result.error);
                Sentry.captureMessage(result.error.toString());
                setShowError(true);
            } else if (result.data?.messageSend?.error) {
                console.error(result.data.messageSend.error);
                Sentry.captureMessage(result.data.messageSend.error);
                setShowError(true);
            } else {
                dispatch(closeModal());
            }
        },
        [message?.id, executeMutation],
    );

    const initialValues = message ? { content: "", subject: `Re: ${message.subject}` } : defaultValues;

    return (
        <>
            <h2>{t("player.messages.heading-send-message")}</h2>
            {!!message && (
                <div>
                    <hr />
                    <H3>{t("player.messages.heading-replying-to", { sender: message.senderName })}</H3>
                    <div>{message.subject}</div>
                    <hr />
                    <RenderContent content={message.content} contentFormat={message.contentFormat} />
                    <hr />
                </div>
            )}
            <Form onSubmit={onSubmit} validate={validate} initialValues={initialValues}>
                {({ handleSubmit, submitting, pristine }) => (
                    <form onSubmit={handleSubmit}>
                        <Input name="subject" label={t("player.messages.form-message-label-subject")} />
                        <Textarea name="content" />
                        <button type="submit" disabled={submitting || pristine}>
                            {t("player.messages.form-message-submit")}
                        </button>
                    </form>
                )}
            </Form>
            {showError && <p>{t("player.messages.error-failed-to-send-message")}</p>}
        </>
    );
}

export default Send;
